import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  Users, 
  CheckCircle2,
  TrendingUp,
  Calendar,
  RefreshCw,
  Download,
  Eye
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HRTeamPerformanceTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/chro-enhanced/hr-team-performance`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching HR team data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { hrLeaders, summary } = data;

  const regionFlags = {
    usa: '🇺🇸🇨🇦',
    jamaica: '🇯🇲',
    barbados: '🇧🇧',
    trinidad_tobago: '🇹🇹',
    panama: '🇵🇦'
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">HR Team Effectiveness</h2>
          <p className="text-sm text-muted-foreground">Regional HR leader performance and workload metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Calendar className="w-4 h-4 mr-2" />
            Schedule HR Review
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-[#1a3a3a]" />
              <span className="text-sm font-medium text-muted-foreground">HR Leaders</span>
            </div>
            <p className="text-3xl font-bold text-[#1a3a3a]">{summary.totalHRLeaders}</p>
            <p className="text-sm text-muted-foreground">managing regional operations</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium text-muted-foreground">Avg Dev Plan Completion</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{summary.avgDevPlanCompletion}%</p>
            <Progress value={summary.avgDevPlanCompletion} className="h-2 mt-2" />
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">Avg Employee Engagement</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{summary.avgEngagement}%</p>
            <Progress value={summary.avgEngagement} className="h-2 mt-2" />
          </CardContent>
        </Card>
      </div>

      {/* HR Leaders Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Regional HR Leaders</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left p-3 text-sm font-semibold">HR Leader</th>
                <th className="text-center p-3 text-sm font-semibold">Employees</th>
                <th className="text-center p-3 text-sm font-semibold">Dev Plans</th>
                <th className="text-center p-3 text-sm font-semibold">Engaged</th>
                <th className="text-center p-3 text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {hrLeaders.map((leader, i) => (
                <tr key={i} className="border-b hover:bg-slate-50">
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#7C3AED] rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {leader.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <p className="font-semibold">{leader.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <span>{regionFlags[leader.region]}</span>
                          {leader.title}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <span className="text-xl font-bold">{leader.employees}</span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">{leader.devPlans} ({leader.devPlansPercentage}%)</span>
                      <Progress value={leader.devPlansPercentage} className="w-20 h-2 mt-1" />
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold">{leader.engaged} ({leader.engagedPercentage}%)</span>
                      <Progress value={leader.engagedPercentage} className="w-20 h-2 mt-1" />
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <Button size="sm" variant="outline">
                        <Eye className="w-3 h-3 mr-1" />
                        View
                      </Button>
                      <Button size="sm" variant="outline">
                        <Calendar className="w-3 h-3 mr-1" />
                        Schedule
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Workload Distribution */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Workload Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            {hrLeaders.map((leader, i) => {
              const totalEmployees = hrLeaders.reduce((sum, l) => sum + l.employees, 0);
              const percentage = Math.round(leader.employees / totalEmployees * 100);
              const colors = ['#3b82f6', '#10b981', '#f59e0b'];
              
              return (
                <div key={i} className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{regionFlags[leader.region]} {leader.name.split(' ')[0]}</span>
                    <span className="text-sm font-bold">{percentage}%</span>
                  </div>
                  <div 
                    className="h-8 rounded-lg flex items-center justify-center text-white font-semibold text-sm"
                    style={{ backgroundColor: colors[i] }}
                  >
                    {leader.employees} employees
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
